import { cn } from '@/lib/utils';

/**
 * A single contact entry with a label and phone number.
 */
export interface HelpfulContactItem {
  label: string;
  phone: string;
}

/**
 * Content structure for the HelpfulContactsCard component.
 */
export interface HelpfulContactsCardContent {
  heading: string;
  contacts: HelpfulContactItem[];
}

/**
 * Props for the HelpfulContactsCard component.
 */
export interface HelpfulContactsCardProps {
  content?: HelpfulContactsCardContent;
  /** Whether font enlargement is active */
  isFontEnlarged?: boolean;
  /**
   * HTML heading level for the card heading.
   * Use to ensure proper heading hierarchy on the page.
   * @default 'h3'
   */
  headingLevel?: 'h2' | 'h3' | 'h4';
  className?: string;
}

/**
 * HelpfulContactsCard - Displays a card with a list of important phone numbers
 * for aging-related services. Each phone number is a clickable tel: link.
 *
 * @example
 * ```tsx
 * import { HelpfulContactsCard } from '@/components';
 *
 * <HelpfulContactsCard
 *   content={{
 *     heading: "Helpful contacts",
 *     contacts: [
 *       { label: "Ohio Department of Aging:", phone: "1-800-266-4346" },
 *       { label: "211 Ohio:", phone: "1-888-644-6211" },
 *     ]
 *   }}
 * />
 * ```
 */
export function HelpfulContactsCard({ content, isFontEnlarged = false, headingLevel = 'h3', className }: HelpfulContactsCardProps) {
  const Heading = headingLevel;
  if (!content) return null;

  return (
    <div
      className={cn(
        'bg-white border border-[#EBEBEB] rounded-[4px]',
        className,
      )}
      style={{
        padding: '16px',
        minHeight: isFontEnlarged ? 'auto' : '258px',
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Heading
        style={{
          fontFamily: "'Source Sans Pro', sans-serif",
          fontWeight: 600,
          fontSize: isFontEnlarged ? '32px' : '24px',
          lineHeight: isFontEnlarged ? '100%' : '32px',
          color: '#000000',
          margin: 0,
        }}
      >
        {content.heading}
      </Heading>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          marginTop: isFontEnlarged ? '12px' : 'auto',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: isFontEnlarged ? '16px' : '8px',
        }}
      >
        {content.contacts.map((contact, index) => (
          <li
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isFontEnlarged ? '4px' : '2px',
            }}
          >
            <span
              style={{
                fontFamily: "'Source Sans Pro', sans-serif",
                fontWeight: isFontEnlarged ? 400 : 600,
                fontSize: isFontEnlarged ? '32px' : '16px',
                lineHeight: isFontEnlarged ? '120%' : '24px',
                color: '#333333',
                display: 'block',
              }}
            >
              {contact.label}
            </span>
            <a
              href={`tel:${contact.phone}`}
              className="helpful-contacts-card-link"
              style={{
                fontFamily: "'Source Sans Pro', sans-serif",
                fontWeight: isFontEnlarged ? 400 : 600,
                fontSize: isFontEnlarged ? '32px' : '20px',
                lineHeight: isFontEnlarged ? '100%' : '28px',
                color: '#0A4D8F',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              {contact.phone}
            </a>
          </li>
        ))}
      </ul>
      <style>{`
        .helpful-contacts-card-link:hover {
          text-decoration: underline !important;
        }
        .helpful-contacts-card-link:focus-visible {
          outline: 2px solid #0A4D8F;
          outline-offset: 2px;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}

HelpfulContactsCard.displayName = 'HelpfulContactsCard';
